import type { LiveComposerEvent, StaffNumber } from '../../CreateMusicXml';

/** Keys pressed within this window belong to one chord, matching live capture. */
export const STEP_CHORD_WINDOW_MS = 55;

export type StepPlacement = {
  cursorDivisions: number;
  durationDivisions: number;
  staff?: StaffNumber;
  /** The composition's hard end; a step that would pass it is refused, not truncated. */
  limitDivisions: number;
};

const validMidi = (midi: number) => Number.isInteger(midi) && midi >= 0 && midi <= 127;

const assertFits = (placement: StepPlacement) => {
  if (!Number.isFinite(placement.durationDivisions) || placement.durationDivisions <= 0)
    throw new Error('Choose a note value before entering notes.');
  if (placement.cursorDivisions + placement.durationDivisions > placement.limitDivisions)
    throw new Error('This edit would exceed the 96-bar composition limit.');
};

/**
 * Step entry: the chosen note value is placed at the cursor with the pitches just
 * played, and the cursor moves on by that value. Timing comes from the note value,
 * never from when the keys were pressed, which is the point of entering by step.
 */
export function placeStepChord(
  events: LiveComposerEvent[],
  midiNotes: number[],
  placement: StepPlacement,
  id: string,
): { events: LiveComposerEvent[]; event: LiveComposerEvent; nextCursorDivisions: number } {
  assertFits(placement);
  const pitches = Array.from(new Set(midiNotes.filter(validMidi))).sort((a, b) => a - b);
  if (pitches.length === 0) throw new Error('Play at least one note to enter it.');
  const event: LiveComposerEvent = {
    id,
    midiNotes: pitches,
    capturedAtMs: 0,
    source: 'manual',
    restBeforeDivisions: 0,
    startDivisions: placement.cursorDivisions,
    durationDivisions: placement.durationDivisions,
    adaptiveTiming: false,
    ...(placement.staff ? { staff: placement.staff } : {}),
  };
  return {
    events: [...events, event],
    event,
    nextCursorDivisions: placement.cursorDivisions + placement.durationDivisions,
  };
}

/** A key pressed just after the first joins the chord already placed. */
export function addStepChordNote(
  events: LiveComposerEvent[],
  eventId: string,
  midi: number,
): LiveComposerEvent[] {
  if (!validMidi(midi)) return events;
  return events.map((event) =>
    event.id === eventId && !event.midiNotes.includes(midi)
      ? { ...event, midiNotes: [...event.midiNotes, midi].sort((a, b) => a - b) }
      : event,
  );
}

/** A rest is silence of the chosen value: nothing is written, the cursor moves on. */
export function placeStepRest(placement: StepPlacement): number {
  assertFits(placement);
  return placement.cursorDivisions + placement.durationDivisions;
}
